import { Box } from '@mui/system';
import { Typography, Stack, InputLabel, TextField, Paper, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, ToggleButton, Checkbox, FormControlLabel } from '@mui/material';
import { useGetCardOwnerQuery, useUpdateCardOwnerMutation, useDeleteCardOwnerMutation, useEnableCardOwnerMutation} from '../../api/cardSlice'
import { useParams, useNavigate } from 'react-router';
import React, { useState, useMemo } from 'react';
import { useGetCardOwnerSpecialtiesQuery } from '../../api/userSlice';
import CreateAutocomplete from '../CustomComponents/CreateAutocomplete';
import MobilePhoneStatus from '../CustomComponents/MobilePhoneStatus';
import { useExpireCardOwnerTokensMutation } from '../../api/apiSlice';
import { useGetHospitalsInOrganizationQuery } from '../../api/organizationSlice';

function EditPhysician(){
    let params = useParams();
    const navigate = useNavigate();
    const { data: physician, isSuccess } = useGetCardOwnerQuery(params.physicianId);
    const [submitUpdateCardOwner] = useUpdateCardOwnerMutation();
    const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
    const redirectPath = `/superSettings/organizations/${params.organizationId}/physicians`;

    const updateCardOwner = async(data) => {
        var requestSpecialty = data.specialty;
        if (typeof(data.specialty) !== 'string') {
            requestSpecialty = data.specialty.name;
        }

        await submitUpdateCardOwner({
            cardOwner : physician,
            firstName: data.firstName,
            lastName: data.lastName,
            specialty : requestSpecialty,
            notes : data.notes,
            phoneNumber : data.phoneNumber,
            email : data.email,
            doNotText : data.doNotText,
            excludeFromCampaigns: data.excludeFromCampaigns
        });
        navigate(redirectPath);
    }

    return (
        isSuccess &&
        <Stack spacing={2} px={{ xs: 2, md: 4 }}>
            <Typography variant='heavy' sx={{ fontSize: '20px' }}>Edit Physician</Typography>
            <Stack direction={{ xs: 'column', lg: 'row' }} spacing={3} alignItems='flex-start'>
                <PhysicianDetail
                    isConfirmationOpen={isConfirmationOpen}
                    setIsConfirmationOpen={setIsConfirmationOpen}
                    updateCardOwner={updateCardOwner}
                    physician={physician}
                    redirectPath={redirectPath}
                />
                <PhysicianHospitals
                    organizationId={params.organizationId}
                    physician={physician}
                />
            </Stack>
        </Stack>
    )
}

export default EditPhysician;

function PhysicianDetail(props){
    const [deleteCardOwner] = useDeleteCardOwnerMutation();
    const [enableCardOwner] = useEnableCardOwnerMutation();
    const [expireActiveTokens] = useExpireCardOwnerTokensMutation();
    const [isExpireConfirmationOpen, setIsExpireConfirmationOpen] = useState(false);
    const navigate = useNavigate();

    const handleDelete = async () => {
        await deleteCardOwner({ 
            cardOwner : props.physician
        });
        navigate(props.redirectPath);
    }
    const handleEnable = async () => {
        await enableCardOwner({
            cardOwner : props.physician
        });
        navigate(props.redirectPath);
    }

    const handleExpireTokens = async () => {
        await expireActiveTokens(props.physician.id);
    }

    const [firstName, setFirstName] = useState(props.physician.first_name);
    const [lastName, setLastName] = useState(props.physician.last_name);
    const [specialty, setSpecialty] = useState(props.physician.specialty ? props.physician.specialty : '');
    const [notes, setNotes] = useState(props.physician.notes ? props.physician.notes : '');
    const [phoneNumber, setPhoneNumber] = useState(props.physician.phone_number);
    const [email, setEmail] = useState(props.physician.email ? props.physician.email : '');
    const [doNotText, setDoNotText] = useState(Boolean(props.physician.do_not_text));
    const [excludeFromCampaigns, setExcludeFromCampaigns] = useState(Boolean(props.physician.exclude_from_campaigns));

    var originalSpecialty = props.physician.specialty ? props.physician.specialty : '';
    const canUpdate = useMemo(() => {
        return (firstName !== props.physician.first_name || lastName !== props.physician.last_name || specialty !== originalSpecialty || phoneNumber !== props.physician.phone_number || email !== props.physician.email || doNotText !== Boolean(props.physician.do_not_text) || excludeFromCampaigns !== Boolean(props.physician.exclude_from_campaigns) || notes !== (props.physician.notes ? props.physician.notes : ''));
    }, [firstName, lastName, specialty, phoneNumber, email, doNotText, excludeFromCampaigns, notes]);

    return(
        <Paper elevation={2} sx={{ width: { xs: '100%', md: '50%' } }}>
            <Stack spacing={4} px={4} pt={4}> 
                <Box>
                    <InputLabel htmlFor="cardOwner-first-name">
                        First Name
                    </InputLabel>
                    <TextField 
                        id="cardOwner-first-name"
                        variant='outlined' 
                        fullWidth={true}
                        value={firstName}
                        onChange={(event) => setFirstName(event.target.value)}
                    />
                </Box>
                <Box>
                    <InputLabel htmlFor="cardOwner-last-name">
                        Last Name
                    </InputLabel>
                    <TextField 
                        id="cardOwner-last-name"
                        variant='outlined' 
                        fullWidth={true}
                        value={lastName}
                        onChange={(event) => setLastName(event.target.value)}
                    />
                </Box>
                <Box>
                    <InputLabel htmlFor="cardOwner-specialty">
                        Specialty
                    </InputLabel>
                    <CreateAutocomplete
                        label="Specialty"
                        value={specialty}
                        setValue={setSpecialty}
                        loadOptions={() => useGetCardOwnerSpecialtiesQuery(props.physician.hospital.id)}
                        fullWidth={true}
                    />
                </Box>
                <Box>
                    <InputLabel htmlFor="cardOwner-external-id">
                        External ID
                    </InputLabel>
                    <TextField
                        id="cardOwner-external-id"
                        variant='outlined'
                        fullWidth={true}
                        disabled
                        value={props.physician.external_id ? props.physician.external_id : ''}
                    />
                </Box>
                <Box>
                    <InputLabel htmlFor="cardOwner-phone-number">
                        Phone Number 
                    </InputLabel>
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <TextField 
                            id="cardOwner-phone-number"
                            variant='outlined' 
                            fullWidth={true}
                            value={phoneNumber}
                            onChange={(event) => setPhoneNumber(event.target.value)}
                        />
                        <MobilePhoneStatus physician={props.physician} />
                    </Stack>
                </Box>
                <Box>
                    <InputLabel htmlFor="cardOwner-email">
                        Email Address
                    </InputLabel>
                    <Stack direction='row' spacing={1} alignItems='center'>
                        <TextField 
                            id="cardOwner-email"
                            variant='outlined' 
                            fullWidth={true}
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                        />
                    </Stack>
                </Box>
                <Box>
                    <InputLabel htmlFor="cardOwner-notes">
                        Notes
                    </InputLabel>
                    <TextField 
                        id="cardOwner-notes"
                        variant='outlined' 
                        fullWidth={true}
                        multiline
                        rows={2}
                        value={notes}
                        onChange={(event) => setNotes(event.target.value)}
                    />
                </Box>
                <Stack direction='row' spacing={2} alignItems='center'>
                    <Typography variant='heavy' sx={{ fontSize: '16px' }}>Texting Status:</Typography>
                    <ToggleButton
                        value="check"
                        selected={doNotText}
                        onChange={() => setDoNotText(!doNotText)}
                    >
                        {doNotText ? 'Disabled' : 'Enabled'}
                    </ToggleButton>
                </Stack>
                <Stack direction='row' spacing={2} alignItems='center'>
                    <Typography variant='heavy' sx={{ fontSize: '16px' }}>Included in Campaigns?:</Typography>
                    <ToggleButton
                        value="check"
                        selected={excludeFromCampaigns}
                        onChange={() => setExcludeFromCampaigns(!excludeFromCampaigns)}
                    >
                        {excludeFromCampaigns ? 'No' : 'Yes'}
                    </ToggleButton>
                </Stack>
                <Stack direction='row' spacing={2} alignItems='flex-start'>
                    <Button 
                        variant='text' 
                        color={props.physician.is_disabled ? 'primary' : 'error'}
                        onClick={() => props.setIsConfirmationOpen(true)}
                    >
                        {props.physician.is_disabled ? 'Enable Account' : 'Disable Account'}
                    </Button>
                    <Button 
                        variant='outlined' 
                        onClick={() => setIsExpireConfirmationOpen(true)}
                    >
                        Expire Tokens
                    </Button>
                    <UpdatePhysicianConfirmation 
                        open={props.isConfirmationOpen} 
                        setOpen={props.setIsConfirmationOpen} 
                        confirmUpdate={props.physician.is_disabled ? handleEnable : handleDelete}
                        isEnabling={props.physician.is_disabled}
                    />
                    <ExpireTokensConfirmation
                        open={isExpireConfirmationOpen}
                        setOpen={setIsExpireConfirmationOpen}
                        confirmExpire={handleExpireTokens}
                    />
                </Stack>
                <Stack direction='row' spacing={2} justifyContent='flex-end' alignItems='center' pb={2} pr={2}>
                    <Button 
                        variant='contained' 
                        onClick={() => props.updateCardOwner({firstName: firstName, lastName: lastName, specialty : specialty, phoneNumber: phoneNumber, email: email, doNotText: doNotText, excludeFromCampaigns: excludeFromCampaigns, notes: notes })}
                        disabled = {!canUpdate}>Update Physician
                    </Button>
                </Stack>
            </Stack>
        </Paper>
    )
}

function PhysicianHospitals({ organizationId, physician }) {
    const { data: hospitalsResponse = [] } = useGetHospitalsInOrganizationQuery(organizationId, {
        skip: !organizationId
    });

    const hospitals = useMemo(() => {
        const hospitalData = hospitalsResponse?.data || hospitalsResponse || [];
        return hospitalData.slice().sort((a, b) => a.name.localeCompare(b.name));
    }, [hospitalsResponse]);

    const physicianHospitalIds = useMemo(() => {
        if (Array.isArray(physician?.hospitals)) {
            return physician.hospitals.map((hospital) => typeof hospital === 'object' ? hospital.id : hospital);
        }
        if (physician?.hospital?.id) {
            return [physician.hospital.id];
        }
        return [];
    }, [physician]);

    return (
        <Paper elevation={2} sx={{ width: { xs: '100%', md: '45%' } }}>
            <Stack spacing={2} px={4} py={4}>
                <Typography variant='heavy' sx={{ fontSize: '20px' }}>Physician Hospitals</Typography>
                <Stack spacing={1}>
                    {hospitals.map((hospital) => (
                        <FormControlLabel
                            key={hospital.id}
                            label={hospital.name}
                            control={<Checkbox checked={physicianHospitalIds.includes(hospital.id)} disabled />}
                        />
                    ))}
                </Stack>
            </Stack>
        </Paper>
    );
}

function UpdatePhysicianConfirmation(props) {
    function handleClose() {
        props.setOpen(false);
    }

    function handleConfirm() {
        props.confirmUpdate();
    }

    return (
        <Dialog open={props.open} onClose={handleClose}>
            <DialogTitle>
            Confirm Card Owner {props.isEnabling ? 'Restoration' : 'Deletion'}
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Are you sure you want to {props.isEnabling ? 'restore' : 'delete'} this card owner?
                </DialogContentText>
                <DialogContentText>
                    {props.isEnabling ? 'Restoring this physician will also restore their preference cards.' : 'Deleting this physician will also delete their preference cards.'}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color='error'>Cancel</Button>
                <Button onClick={handleConfirm} color='primary'>Confirm</Button>
            </DialogActions>
        </Dialog>
    );
}

function ExpireTokensConfirmation({ open , setOpen, confirmExpire }) {
    function handleClose() {
        setOpen(false);
    }

    function handleConfirm() {
        confirmExpire();
        setOpen(false);
    }

    return (
        <Dialog open={open} onClose={handleClose}>
            <DialogTitle>
            Expire Active Tokens
            </DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Are you sure you want to expire all active tokens for this physician? This will prevent the physician from accessing any DOCSI physician dashboard until a new tokenized link is generated and delivered to them.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose} color='error'>Cancel</Button>
                <Button onClick={handleConfirm} color='primary'>Confirm</Button>
            </DialogActions>
        </Dialog>
    );
}

